import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { TabStyles } from '../../styles/TabStyles';
import { makeTestId } from '../../utils/Declarative';
import { generateId } from '../../utils/DomUtil';
import { DropdownButton, DropdownButtonProps, DropdownButtonType } from '../Dropdown/DropdownButton';
import { ColorGridPreset, ColorGridWidget, ColorGridWidgetProps } from './ColorGridWidget';
import { ColorPickerPresets } from './ColorPickerPresets';
import { CustomColorInput } from './CustomColorInputWidget';

export interface ColorPickerWidgetProps extends
  Pick<DropdownButtonProps, 'disabled' | 'focusOnClose' | 'label' | 'popupAlignment' | 'stackingRootId' | 'zIndex'>,
  Pick<ColorGridWidgetProps, 'colorsPreset' | 'selectedColor'> {

  /** @deprecated use `onColorSelected` */ handleColorSelected?: (color: string) => void;
  /** @deprecated use `focusOnClose` */ shouldReturnFocusOnClose?: boolean;
  /** @deprecated use `zIndex` */ dropdownZIndex?: number;

  /**
   * The width of the color ribbon that will appear in the button
   */
  colorLabelWidth?: number;

  /**
   * Callback function to be called when a color is picked from the grid
   */
  onColorSelected: (color: string) => void;

  /**
   * whether or not to show the custom color input widget at the bottom of the color picker
   */
  showCustomColorInput?: boolean;

  /**
   * Test id to be attached to the dialog and its contents.
   * The suffix `'-ColorPicker'` will be automatically added
   */
  testId: string;

  /**
   * Tooltip text to be shown by the browser when user hovers the element
   */
  title?: string;
}

function isSelectedColorCustom(presetGridColors: String[], selectedColor: string): boolean {
  for (let color of presetGridColors) {
    if (color.toLowerCase() === selectedColor.toLowerCase()) {
      return false;
    }
  }
  return true;
}

function getCustomColorInput(selectedColor: string | undefined, colorsPreset: ColorGridPreset): string {
  if (!selectedColor) { return ''; }
  let colors: String[] = colorsPreset === ColorGridPreset.Light ?
    ColorPickerPresets.Light : ColorPickerPresets.Dark;
  return isSelectedColorCustom(colors, selectedColor) ? selectedColor : '';
}

export const ColorPickerWidget = React.forwardRef<DropdownButton, ColorPickerWidgetProps>((props, ref) => {
  const dialogId = React.useState(generateId())[0];
  const closeFn = React.useRef<(() => void) | null>(null);
  const colorGridElement = React.useRef<HTMLElement | null>();

  const onColorSelected = (color: string) => {
    closeFn.current?.();
    // tslint:disable-next-line:deprecation
    (props.onColorSelected || props.handleColorSelected)?.(color.toLowerCase());
  };

  return <div style={{ display: 'inline-block' }} data-tb-test-id={makeTestId(props.testId, 'ColorPicker')}>
    <DropdownButton
      aria-controls={dialogId}
      aria-haspopup='dialog'
      data-tb-test-id={makeTestId(props.testId, 'ColorPickerButton')}
      disabled={props.disabled}
      // tslint:disable-next-line:deprecation
      focusOnClose={props.focusOnClose || props.shouldReturnFocusOnClose}
      label={props.label}
      onOpen={doneFn => closeFn.current = doneFn}
      popupAlignment={props.popupAlignment}
      popupContent={<ColorGridPopupContent
        colorsPreset={props.colorsPreset}
        disabled={props.disabled}
        handleSwatchClicked={onColorSelected}
        id={dialogId}
        ref={gridRef => colorGridElement.current = ReactDOM.findDOMNode(gridRef) as HTMLElement}
        selectedColor={props.selectedColor}
        showCustomColorInput={props.showCustomColorInput}
        testId={props.testId}
      />}
      ref={ref}
      // tslint:disable-next-line:deprecation
      stackingRootId={props.stackingRootId}
      testId={null!} // overridden by data-tb-test-id above
      title={props.title}
      type={DropdownButtonType.Outline}
      // tslint:disable-next-line:deprecation
      zIndex={props.zIndex || props.dropdownZIndex}
    >
      <div style={{
        backgroundColor: (props.selectedColor || '').toLowerCase(),
        boxSizing: 'border-box',
        display: 'inline-block',
        height: '50%',
        width: props.colorLabelWidth || 40,
      }} />
    </DropdownButton>
  </div >;
});
ColorPickerWidget.defaultProps = { colorsPreset: ColorGridPreset.Dark, focusOnClose: true };
ColorPickerWidget.displayName = 'ColorPickerWidget';

type ColorGridPopupProps = Omit<ColorGridWidgetProps, 'gridType'> & { id: string, showCustomColorInput?: boolean };
const ColorGridPopupContent = React.forwardRef<ColorGridWidget, ColorGridPopupProps>((props, ref) =>
  <div
    data-tb-test-id={makeTestId(props.testId, 'ColorGridWrapper')}
    id={props.id}
    role='dialog'
    style={{ padding: TabStyles.Spacing.LargeSpace }}
  >
    <ColorGridWidget
      colorsPreset={props.colorsPreset}
      disabled={props.disabled}
      handleSwatchClicked={props.handleSwatchClicked}
      ref={ref}
      selectedColor={(props.selectedColor || '').toLowerCase()}
      testId='colorgrid'
    />
    {props.showCustomColorInput && <>
      <hr style={{ marginTop: TabStyles.Spacing.LargeSpace }} />
      <CustomColorInput
        color={getCustomColorInput(props.selectedColor, props.colorsPreset!)}
        onColorSelected={props.handleSwatchClicked}
        testId={props.testId}
      />
    </>}
  </div>
);
ColorGridPopupContent.displayName = 'ColorGridPopupContent';
